import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RotateCw, ShoppingCart, PlusCircle, MinusCircle } from 'feather-icons-react/build/IconComponents';
import { Trash2 } from 'react-feather';
import { printBill } from '../../utils/billPrinter';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useAuth } from '../../core/context/AuthContext';
import { getImageUrl } from '../../utils/imageUrl';

/** Currency-safe rounding (2 dp) for POS totals */
const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const Pos = () => {
  const { user } = useAuth();
  const MySwal = withReactContent(Swal);
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [categories, setCategories] = useState([]);
  const [productList, setProductList] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Variation popup state
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [variationProduct, setVariationProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [variationQty, setVariationQty] = useState(1);

  // Order settings
  const [orderType, setOrderType] = useState("Dine-in");
  const [tableNumber, setTableNumber] = useState("");
  const [customerName, setCustomerName] = useState("Walk-in Customer");
  const [paymentMethod] = useState("Cash");

  const [orderTax] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [discountRate] = useState(0);

  // "Add More" mode â€“ appending to existing order
  const [addToOrderId, setAddToOrderId] = useState(null);
  const [addToOrder, setAddToOrder] = useState(null); // existing order details

  // Read ?addTo=<orderId> from URL and load the existing order
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('addTo');
    if (orderId) {
      setAddToOrderId(orderId);
      axios.get(`https://avastar-pos-backend.onrender.com/api/orders/${orderId}`)
        .then(res => {
          const ord = res.data;
          setAddToOrder(ord);
          // Pre-fill order type and table from existing order
          setOrderType(ord.orderType || 'Dine-in');
          setTableNumber(ord.tableNumber || '');
          setCustomerName(ord.customerName || 'Walk-in Customer');
        })
        .catch(() => MySwal.fire('Error', 'Could not load existing order', 'error'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories/active?type=Menu");
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/products/available");
      setProductList(res.data);
    } catch (err) {
      console.error("Error fetching products", err);
    }
  };

  const filteredProducts = productList.filter((product) => {
    const matchesSearch = product.productName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category?.name === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // â”€â”€ Item click handler: opens variation popup or adds directly â”€â”€
  const handleItemClick = (product) => {
    if (product.variations && product.variations.length > 0) {
      setVariationProduct(product);
      setSelectedVariation(null);
      setVariationQty(1);
      setShowVariationModal(true);
    } else {
      addToCart(product, null, product.price);
    }
  };

  // â”€â”€ Add to cart (with optional variation) â”€â”€
  const addToCart = (product, variationName, price, qty = 1) => {
    const cartKey = variationName ? `${product._id}_${variationName}` : product._id;
    const displayName = variationName ? `${product.productName} (${variationName})` : product.productName;

    const existing = cartItems.find((item) => item.cartKey === cartKey);
    if (existing) {
      setCartItems(
        cartItems.map((item) =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + qty, total: roundMoney((item.quantity + qty) * item.price) }
            : item
        )
      );
    } else {
      setCartItems([
        ...cartItems,
        {
          cartKey,
          product: product._id,
          productName: displayName,
          variationName: variationName || "",
          price: price,
          quantity: qty,
          total: roundMoney(price * qty),
          notes: "",
        },
      ]);
    }
  };

  // â”€â”€ Add from variation modal â”€â”€
  const addVariationToCart = () => {
    if (!selectedVariation) return;
    addToCart(variationProduct, selectedVariation.name, selectedVariation.price, variationQty);
    setShowVariationModal(false);
    setVariationProduct(null);
    setSelectedVariation(null);
    setVariationQty(1);
  };

  const updateCartQty = (cartKey, amount) => {
    setCartItems(
      cartItems.map((item) => {
        if (item.cartKey === cartKey) {
          const newQty = item.quantity + amount;
          if (newQty > 0) {
            return { ...item, quantity: newQty, total: roundMoney(newQty * item.price) };
          }
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  const removeCartItem = (cartKey) => {
    setCartItems(cartItems.filter((item) => item.cartKey !== cartKey));
  };

  const handleItemNoteChange = (cartKey, note) => {
    setCartItems(cartItems.map(item =>
      item.cartKey === cartKey ? { ...item, notes: note } : item
    ));
  };

  const calculateSubTotal = () => roundMoney(cartItems.reduce((sum, item) => sum + item.total, 0));
  const calculateTotalOrderTax = () => roundMoney(calculateSubTotal() * orderTax);
  const calculateTotalDiscount = () => roundMoney(calculateSubTotal() * discountRate);
  const calculateGrandTotal = () => {
    let total = calculateSubTotal() + calculateTotalOrderTax() - calculateTotalDiscount();
    if (orderType === "Delivery") total += roundMoney(shippingCost);
    return roundMoney(total);
  };

  // â”€â”€ Silent thermal KOT print â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const printKOTSilent = (order) => {
    if (!order) return;
    printBill(order, 'kot');
  };

  // â”€â”€ Place Order (or Add More to existing) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const placeOrder = async () => {
    if (cartItems.length === 0) {
      MySwal.fire("Error", "Cart is empty", "error");
      return;
    }

    const itemsPayload = cartItems.map(item => ({
      product: item.product,
      variationName: item.variationName || "",
      quantity: item.quantity,
      notes: item.notes,
    }));

    try {
      if (addToOrderId) {
        // â”€â”€ ADD MORE mode: append to existing order â”€â”€
        const res = await axios.patch(
          `https://avastar-pos-backend.onrender.com/api/orders/${addToOrderId}/add-items`,
          { items: itemsPayload }
        );
        const updatedOrder = res.data.order;
        const addedItems = res.data.addedItems;

        // Print KOT only for the newly added items
        const kotData = {
          ...updatedOrder,
          items: addedItems,            // only new items on this KOT
          kotNumber: `${updatedOrder.kotNumber}+`,
        };
        printBill(kotData, 'kot');

        setCartItems([]);
        MySwal.fire({
          icon: 'success',
          title: 'Items Added!',
          text: `${addedItems.length} item(s) added to ${updatedOrder.orderId}. KOT sent to kitchen.`,
          confirmButtonText: 'Go to Active Orders',
          showCancelButton: true,
          cancelButtonText: 'Add More'
        }).then(result => {
          if (result.isConfirmed) navigate('/restaurant/orders');
        });
      } else {
        // â”€â”€ NORMAL mode: create new order â”€â”€
        const payload = {
          items: itemsPayload,
          taxRate: orderTax,
          discountRate,
          shipping: orderType === "Delivery" ? roundMoney(shippingCost) : 0,
          paymentMethod,
          customerName,
          orderType,
          tableNumber: orderType === "Dine-in" && tableNumber ? tableNumber : "",
          createdBy: user?._id || null,
        };

        const res = await axios.post("https://avastar-pos-backend.onrender.com/api/orders", payload);
        const newOrder = res.data.order;
        setCartItems([]);
        setTableNumber("");
        setTimeout(() => printKOTSilent(newOrder), 300);
      }
    } catch (err) {
      console.error(err);
      MySwal.fire("Error", err.response?.data?.message || "Failed to process order", "error");
    }
  };

  const resetOrder = () => {
    setCartItems([]);
    setTableNumber("");
    setCustomerName("Walk-in Customer");
  };

  const getCatIcon = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("beverage") || n.includes("drink") || n.includes("juice")) return "fa-wine-glass-alt";
    if (n.includes("dessert") || n.includes("sweet") || n.includes("cake")) return "fa-ice-cream";
    if (n.includes("rice")) return "fa-seedling";
    if (n.includes("kottu")) return "fa-bread-slice";
    if (n.includes("soup")) return "fa-mug-hot";
    if (n.includes("salad")) return "fa-leaf";
    if (n.includes("fried")) return "fa-fire";
    return "fa-utensils";
  };

  const allCats = [
    { _id: "all", name: "all", label: "All Items", count: productList.length, image: null },
    ...categories.map(c => ({
      _id: c._id,
      name: c.name,
      label: c.name,
      count: productList.filter(p => p.category?.name === c.name).length,
      image: c.image
    }))
  ];

  // â”€â”€ Styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const CART_WIDTH = '340px';

  return (
    <div>
      {/* Inline styles */}
      <style>{`
        .pos-page { display: flex; flex-direction: column; height: calc(100vh - 60px); margin-top: 60px; overflow: hidden; background: var(--pos-bg, #f1f5f9); }
        .pos-topbar { display:flex; align-items:center; justify-content:space-between; padding:5px 12px; background: var(--pos-card-bg, #fff); border-bottom:1px solid var(--pos-border, #e2e8f0); flex-shrink:0; gap:8px; flex-wrap:wrap; }
        .pos-body { display:flex; flex:1; overflow:hidden; }
        .pos-left { flex:1; overflow-y:auto; padding:10px 12px; }
        .pos-right { width:${CART_WIDTH}; min-width:${CART_WIDTH}; max-width:${CART_WIDTH}; background: var(--pos-card-bg, #fff); border-left:1px solid var(--pos-border, #e2e8f0); display:flex; flex-direction:column; height:100%; overflow:hidden; }
        .pos-right .cart-scroll { flex:1; overflow-y:auto; padding:8px 12px; }
        .cat-chip { display:inline-flex; align-items:center; height:38px; gap:8px; padding:0 14px; border-radius:30px; border:1px solid var(--pos-border, #e2e8f0); background: var(--pos-card-bg, #fff); cursor:pointer; margin:4px; font-size:13px; font-weight:600; white-space:nowrap; transition:all .15s; color: var(--pos-text-main, #1e293b); }
        .cat-chip:hover { border-color:#2261ff; }
        .cat-chip.active { background:#2261ff; color:#fff; border-color:#2261ff; }
        .cat-chip .cat-icon { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
        .cat-chip.active .cat-icon { background:rgba(255,255,255,0.3); color:#fff; }
        .cat-chip:not(.active) .cat-icon { background: var(--pos-bg, #f1f5f9); color: var(--pos-text-muted, #64748b); }
        .cat-chip .cat-count { font-size:10px; opacity:.8; margin-left:2px; }
        .item-card { border:1px solid var(--pos-border, #e5e7eb); border-radius:10px; overflow:hidden; background: var(--pos-card-bg, #fff); cursor:pointer; height:100%; transition:box-shadow .2s,transform .15s; }
        .item-card:hover { box-shadow:0 4px 14px rgba(0,0,0,.1); transform:translateY(-2px); }
        .variation-chip { display:inline-flex; align-items:center; padding:10px 18px; border-radius:10px; border:2px solid var(--pos-border, #e2e8f0); background: var(--pos-card-bg, #fff); color: var(--pos-text-main, #1e293b); cursor:pointer; font-size:13px; font-weight:600; transition:all .15s; flex-direction:column; gap:2px; min-width:100px; text-align:center; }
        .variation-chip:hover { border-color:#2261ff; background: var(--pos-hover, #f0f4ff); }
        .variation-chip.selected { border-color:#2261ff; background:#2261ff; color:#fff; }
        .variation-chip .var-price { font-size:14px; font-weight:700; }
        .variation-chip.selected .var-price { color:#fff; }
        .variation-chip:not(.selected) .var-price { color:#2261ff; }
        
        .text-heading { color: var(--pos-text-main, #1e293b); }
        .text-muted-custom { color: var(--pos-text-muted, #94a3b8); }
        .cart-item-box { border: 1px solid var(--pos-border, #f1f5f9); border-radius: 8px; padding: 6px 8px; margin-bottom: 6px; background: var(--pos-item-bg, #fafafa); }
        .pos-modal-content { background: var(--pos-card-bg, #fff); border-radius: 16px; max-width: 480px; width: 95%; box-shadow: 0 25px 50px rgba(0,0,0,0.25); overflow: hidden; animation: slideUp 0.3s ease; }
        .pos-border-top { border-top: 1px solid var(--pos-border, #f1f5f9); }
        .pos-border-bottom { border-bottom: 1px solid var(--pos-border, #f1f5f9); }

        [data-layout-mode="dark_mode"] {
          --pos-bg: #0f172a;
          --pos-card-bg: #1e293b;
          --pos-item-bg: #0f172a;
          --pos-border: #334155;
          --pos-text-main: #f8fafc;
          --pos-text-muted: #94a3b8;
          --pos-hover: #334155;
        }
        [data-layout-mode="dark_mode"] .form-control, [data-layout-mode="dark_mode"] .form-select {
          background-color: var(--pos-item-bg);
          border-color: var(--pos-border);
          color: var(--pos-text-main);
        }
        [data-layout-mode="dark_mode"] .form-control::placeholder { color: var(--pos-text-muted); }
      `}</style>

      <div className="pos-page">

        {/* â”€â”€ TOP BAR â”€â”€ */}
        <div className="pos-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', marginTop: '10px' }}>
            <Link to="/" className="btn btn-secondary btn-sm" style={{ padding: '6px 20px', fontSize: '14px', whiteSpace: 'nowrap', minWidth: '100px' }}>
              <i className="fas fa-arrow-left me-2"></i>Back
            </Link>
            <span className="text-heading" style={{ fontWeight: 700, fontSize: '18px', whiteSpace: 'nowrap' }}>Restaurant POS</span>
            <div className="input-group input-group-sm" style={{ maxWidth: '350px' }}>
              <span className="input-group-text" style={{ padding: '2px 6px', fontSize: '11px' }}><i className="fas fa-search"></i></span>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ fontSize: '13px', padding: '5px 10px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
            <Link to="/restaurant/orders" className="btn btn-secondary btn-sm" style={{ padding: '3px 8px', fontSize: '11px' }}>
              <ShoppingCart style={{ width: '12px', height: '12px', marginRight: '3px' }} />
              Active Orders
            </Link>
            <Link to="#" className="btn btn-info btn-sm" onClick={resetOrder} style={{ padding: '3px 8px', fontSize: '11px' }}>
              <RotateCw style={{ width: '12px', height: '12px', marginRight: '3px' }} />
              Reset
            </Link>
          </div>
        </div>

        {/* â”€â”€ BODY â”€â”€ */}
        <div className="pos-body">

          {/* â”€â”€ LEFT: Categories + Items (scrollable) â”€â”€ */}
          <div className="pos-left">

            {/* Category Chips - flex wrap */}
            <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              {allCats.map(cat => (
                <span
                  key={cat._id}
                  className={`cat-chip ${selectedCategory === cat.name ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.name)}
                >
                  <span className="cat-icon" style={{ overflow: 'hidden' }}>
                    {cat.image ? (
                      <img src={getImageUrl(cat.image)} alt={cat.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <i className={`fas ${cat.name === 'all' ? 'fa-th' : getCatIcon(cat.name)}`}></i>
                    )}
                  </span>
                  {cat.label}
                  <span className="cat-count">({cat.count})</span>
                </span>
              ))}
            </div>

            {/* Items Grid */}
            <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 row-cols-xl-6 g-2">
              {filteredProducts.map((product) => (
                <div className="col" key={product._id}>
                  <div className="item-card" onClick={() => handleItemClick(product)}>
                    {product.image ? (
                      <div style={{ width: '100%', aspectRatio: '1 / 0.75', backgroundImage: `url(${getImageUrl(product.image)})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
                    ) : (
                      <div style={{ width: '100%', aspectRatio: '1 / 0.75', background: 'linear-gradient(135deg,#f8f9fa,#e9ecef)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '1.6rem', color: '#94a3b8' }}><i className="fas fa-utensils"></i></span>
                      </div>
                    )}
                    <div style={{ padding: '5px 7px' }}>
                      <div className="text-muted-custom" style={{ fontSize: '9px' }}>{product.category?.name || 'General'}</div>
                      <div className="text-heading" style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '32px' }}>
                        {product.productName}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '3px' }}>
                        <span className="text-muted-custom" style={{ fontSize: '11px' }}>{product.preparationTime}m</span>
                        {product.variations && product.variations.length > 0 ? (
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2261ff' }}>
                            Rs {Math.min(...product.variations.map(v => v.price))}+
                          </span>
                        ) : (
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#2261ff' }}>Rs {product.price}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="col-12 text-center py-5"><h6 className="text-muted">No items found</h6></div>
              )}
            </div>
          </div>

          {/* ——— RIGHT: Fixed Cart ——— */}
          <div className="pos-right">

            {/* Cart Header */}
            <div className="pos-border-bottom" style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div className="text-heading" style={{ fontWeight: 700, fontSize: '16px' }}>Current Order</div>
                <div className="text-muted-custom" style={{ fontSize: '12px' }}>{new Date().toLocaleDateString()}</div>
              </div>
              <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '12px', padding: '3px 12px' }} onClick={resetOrder}>Clear</button>
            </div>

            {/* Add-More mode banner */}
            {addToOrderId && addToOrder && (
              <div style={{
                background: 'linear-gradient(135deg,#ff9f43,#ff6b35)',
                color: '#fff',
                padding: '8px 12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}>
                <i className="fas fa-link" style={{ fontSize: '14px' }}></i>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>Adding to: {addToOrder.orderId}</div>
                  <div style={{ opacity: 0.85 }}>
                    {addToOrder.orderType} • Table {addToOrder.tableNumber ? ` ${addToOrder.tableNumber}` : 'N/A'}
                    {' '}• {addToOrder.items?.length} existing items
                  </div>
                </div>
                <button
                  onClick={() => navigate('/restaurant/orders')}
                  style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}
                  title="Cancel and go back"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Order Type + Table */}
            <div className="pos-border-bottom" style={{ padding: '8px 12px', flexShrink: 0 }}>
              <div className="row g-2">
                <div className="col-6">
                  <label className="text-muted-custom" style={{ fontSize: '10px', fontWeight: 600 }}>Order Type</label>
                  <select className="form-select form-select-sm" value={orderType} onChange={(e) => setOrderType(e.target.value)} style={{ fontSize: '11px' }}>
                    <option value="Dine-in">Dine-in</option>
                    <option value="Takeaway">Takeaway</option>
                    <option value="Delivery">Delivery</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="text-muted-custom" style={{ fontSize: '10px', fontWeight: 600 }}>
                    {orderType === "Dine-in" ? "Table # (optional)" : "Customer Info"}
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={orderType === "Dine-in" ? "Table #" : "Info"}
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    style={{ fontSize: '11px' }}
                  />
                </div>
              </div>
            </div>

            {/* Cart Items (scrollable area) */}
            <div className="cart-scroll">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span className="text-heading" style={{ fontSize: '12px', fontWeight: 600 }}>Items</span>
                <span className="badge bg-primary ms-2" style={{ fontSize: '9px' }}>{cartItems.length}</span>
              </div>
              {cartItems.map((item) => (
                <div key={item.cartKey} className="cart-item-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="text-heading" style={{ fontSize: '14px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.productName}</div>
                      <div className="text-muted-custom" style={{ fontSize: '12px' }}>Rs {item.price}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      <Link to="#" onClick={() => updateCartQty(item.cartKey, -1)} style={{ lineHeight: 0 }}>
                        <MinusCircle className="text-muted-custom" style={{ width: '20px', height: '20px' }} />
                      </Link>
                      <span className="text-heading" style={{ minWidth: '22px', textAlign: 'center', fontWeight: 700, fontSize: '15px' }}>{item.quantity}</span>
                      <Link to="#" onClick={() => updateCartQty(item.cartKey, 1)} style={{ lineHeight: 0 }}>
                        <PlusCircle style={{ width: '20px', height: '20px', color: '#2261ff' }} />
                      </Link>
                    </div>
                    <div style={{ minWidth: '65px', textAlign: 'right', fontSize: '14px', fontWeight: 700, color: '#2261ff', flexShrink: 0 }}>
                      Rs {item.total}
                    </div>
                    <Link to="#" onClick={() => removeCartItem(item.cartKey)} style={{ lineHeight: 0, flexShrink: 0 }}>
                      <Trash2 style={{ width: '13px', height: '13px', color: '#ef4444' }} />
                    </Link>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Kitchen notes..."
                    value={item.notes}
                    onChange={(e) => handleItemNoteChange(item.cartKey, e.target.value)}
                    style={{ fontSize: '10px', padding: '2px 6px' }}
                  />
                </div>
              ))}
              {cartItems.length === 0 && (
                <div className="text-muted-custom" style={{ textAlign: 'center', padding: '20px 0', fontSize: '12px' }}>
                  Cart is empty. Add items from the menu.
                </div>
              )}
            </div>

            {/* Totals (pinned to bottom) */}
            <div className="pos-border-top" style={{ padding: '8px 12px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                <span className="text-muted-custom">Sub Total</span>
                <span className="text-heading" style={{ fontWeight: 600 }}>Rs {calculateSubTotal().toFixed(2)}</span>
              </div>
              {orderType === "Delivery" && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', marginBottom: '3px' }}>
                  <span className="text-muted-custom">Shipping</span>
                  <input type="number" min="0" step="0.01" className="form-control form-control-sm" style={{ width: '70px', textAlign: 'right', fontSize: '11px' }} value={shippingCost} onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n) || n < 0) return;
                    setShippingCost(n);
                  }} />
                </div>
              )}
              <div className="pos-border-top" style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', marginTop: '4px', fontSize: '15px', fontWeight: 700 }}>
                <span className="text-heading">Total</span>
                <span style={{ color: '#2261ff' }}>Rs {calculateGrandTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Place Order */}
            <div className="pos-border-top" style={{ padding: '8px 12px', flexShrink: 0 }}>
              <button
                className={`btn w-100 ${addToOrderId ? 'btn-warning' : 'btn-primary'}`}
                onClick={placeOrder}
                disabled={cartItems.length === 0}
                style={{ fontWeight: 700, fontSize: '15px', padding: '12px' }}
              >
                {addToOrderId ? (
                  <><i className="fas fa-plus-circle me-2"></i> ADD TO ORDER & PRINT KOT</>
                ) : (
                  <><i className="fas fa-print me-2"></i> PLACE ORDER & PRINT KOT</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ——— VARIATION SELECTION MODAL ——— */}
      {showVariationModal && variationProduct && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.2s ease'
        }}
          onClick={() => setShowVariationModal(false)}
        >
          <div className="pos-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Product Image */}
            <div style={{ display: 'flex', gap: '16px', padding: '20px 20px 0' }}>
              <div style={{
                width: '120px', height: '120px', borderRadius: '12px', flexShrink: 0, overflow: 'hidden',
                background: variationProduct.image
                  ? `url(${getImageUrl(variationProduct.image)}) center/cover`
                  : 'linear-gradient(135deg,#f8f9fa,#e9ecef)'
              }}>
                {!variationProduct.image && (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#94a3b8' }}><i className="fas fa-utensils"></i></div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h5 className="text-heading" style={{ fontWeight: 700, fontSize: '18px', marginBottom: '4px' }}>
                  {variationProduct.productName}
                </h5>
                <p className="text-muted-custom" style={{ fontSize: '13px', marginBottom: '8px', lineHeight: 1.4 }}>
                  {variationProduct.description || variationProduct.category?.name || 'Select a size to add to cart'}
                </p>

                {/* Close button */}
                <button
                  onClick={() => setShowVariationModal(false)}
                  className="text-muted-custom"
                  style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}
                >
                  âœ•
                </button>
              </div>
            </div>

            {/* Sizes / Variations */}
            <div style={{ padding: '16px 20px' }}>
              <h6 className="text-heading" style={{ fontWeight: 700, fontSize: '14px', marginBottom: '12px' }}>
                <i className="fas fa-layer-group me-2 text-primary"></i>Sizes
              </h6>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {variationProduct.variations.map((v, idx) => (
                  <div
                    key={idx}
                    className={`variation-chip ${selectedVariation?.name === v.name ? 'selected' : ''}`}
                    onClick={() => setSelectedVariation(v)}
                  >
                    <span style={{ fontSize: '12px' }}>{v.name}</span>
                    <span className="var-price">Rs {v.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total + Quantity + Add to Cart */}
            <div className="pos-border-top" style={{ padding: '0 20px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                <span className="text-heading" style={{ fontSize: '15px', fontWeight: 700 }}>Total</span>
                <span style={{ fontSize: '20px', fontWeight: 800, color: '#2261ff' }}>
                  Rs {selectedVariation ? (selectedVariation.price * variationQty).toFixed(2) : '0.00'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Qty controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid var(--pos-border, #e2e8f0)', borderRadius: '10px', padding: '6px 12px' }}>
                  <button
                    onClick={() => setVariationQty(Math.max(1, variationQty - 1))}
                    className="text-muted-custom"
                    style={{ background: 'none', border: 'none', fontSize: '18px', fontWeight: 700, cursor: 'pointer', padding: '0 4px' }}
                  >-</button>
                  <span className="text-heading" style={{ fontWeight: 700, fontSize: '16px', minWidth: '24px', textAlign: 'center' }}>{variationQty}</span>
                  <button
                    onClick={() => setVariationQty(variationQty + 1)}
                    style={{ background: 'none', border: 'none', fontSize: '18px', fontWeight: 700, color: '#2261ff', cursor: 'pointer', padding: '0 4px' }}
                  >+</button>
                </div>
                {/* Add to Cart button */}
                <button
                  className="btn btn-primary flex-grow-1"
                  disabled={!selectedVariation}
                  onClick={addVariationToCart}
                  style={{ fontWeight: 700, fontSize: '14px', padding: '10px', borderRadius: '10px', opacity: selectedVariation ? 1 : 0.5 }}
                >
                  <i className="fas fa-cart-plus me-2"></i> Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default Pos;
